import { useMutation, useQuery, useQueryClient } from 'react-query';
import fetch from 'cross-fetch';

import { portalApi } from '../api';
import { ClassificationBusyStatuses, DropboxBusyStatuses, ImportBusyStatuses } from '../constants/importStatuses';
import { getAuthHeaders } from '../utils/baseApi';

import importKeys from './models/imports/keys';
import { camelCaseApiResponse } from './util';

export const IMPORT_POLL_INTERVAL_MS = 3000;

/**
 * Polls while the API is validating, running, rolling back, pulling from Dropbox
 * or labelling, and from the moment one of those is requested until the first
 * poll brings back the API's own busy state.
 */
export const importRefetchInterval = importData => {
  if (!importData) {
    return false;
  }

  const busy = Boolean(importData.pendingAction)
    || ImportBusyStatuses.includes(importData.status)
    || DropboxBusyStatuses.includes(importData.summary?.dropbox?.status)
    || ClassificationBusyStatuses.includes(importData.summary?.classification?.status);

  return busy ? IMPORT_POLL_INTERVAL_MS : false;
};

/* QUERIES
========================================================= */

export const useCompanyImports = (companyId, { pageNumber = 1, pageSize = 25 } = {}, options = {}) => useQuery(
  [...importKeys.list(companyId), { pageNumber }],
  () => camelCaseApiResponse(() => portalApi.listImports(companyId, { pageNumber, pageSize })),
  {
    enabled: Boolean(companyId),
    keepPreviousData: true,
    ...options
  }
);

export const useImport = (importId, options = {}) => useQuery(
  importKeys.detail(importId),
  () => camelCaseApiResponse(() => portalApi.getImport(importId)),
  {
    enabled: Boolean(importId),
    refetchInterval: importRefetchInterval,
    ...options
  }
);

export const useImportRows = (importId, { status = null, pageNumber = 1, pageSize = 50 } = {}, options = {}) => useQuery(
  importKeys.rows(importId, status, pageNumber),
  () => camelCaseApiResponse(() => portalApi.listImportRows(importId, { status, pageNumber, pageSize })),
  {
    enabled: Boolean(importId),
    keepPreviousData: true,
    ...options
  }
);

const ALL_ROWS_PAGE_SIZE = 100;

/**
 * Every row of the import, for views that group them (the pre-run preview).
 * `version` should change whenever the rows do, so a fresh dry run refetches.
 */
export const useAllImportRows = (importId, version = null, options = {}) => useQuery(
  importKeys.allRows(importId, version),
  async () => {
    const fetchPage = pageNumber => camelCaseApiResponse(() => portalApi.listImportRows(importId, { pageNumber, pageSize: ALL_ROWS_PAGE_SIZE }));
    const first = await fetchPage(1);
    const remaining = Array.from({ length: Math.max(0, (first.lastPage ?? 1) - 1) }, (_, index) => fetchPage(index + 2));
    const pages = [first, ...(await Promise.all(remaining))];

    return pages.flatMap(page => page.data ?? []);
  },
  {
    enabled: Boolean(importId),
    ...options
  }
);

export const useImportFiles = (importId, options = {}) => useQuery(
  importKeys.files(importId),
  () => camelCaseApiResponse(() => portalApi.listImportFiles(importId, { pageSize: 5 })),
  {
    enabled: Boolean(importId),
    ...options
  }
);

export const useImportFileList = (importId, { status = null, search = null, pageNumber = 1, pageSize = 25 } = {}, options = {}) => useQuery(
  importKeys.fileList(importId, status, search, pageNumber),
  () => camelCaseApiResponse(() => portalApi.listImportFiles(importId, { status, search, pageNumber, pageSize })),
  {
    enabled: Boolean(importId),
    keepPreviousData: true,
    ...options
  }
);

export const useImportRollbackPreview = (importId, options = {}) => useQuery(
  importKeys.rollbackPreview(importId),
  () => camelCaseApiResponse(() => portalApi.getImportRollbackPreview(importId)),
  {
    enabled: Boolean(importId),
    cacheTime: 0,
    ...options
  }
);

/* MUTATIONS
========================================================= */

const invalidateImport = (queryClient, importId) => {
  queryClient.invalidateQueries(importKeys.detail(importId));
  queryClient.invalidateQueries([...importKeys.all, 'rows', Number(importId)]);
  queryClient.invalidateQueries(importKeys.files(importId));
  queryClient.invalidateQueries([...importKeys.all, 'list']);
};

/**
 * Every state transition returns the updated import, so the detail page shows
 * the new status immediately (and starts polling from it) instead of waiting
 * for the invalidation's refetch.
 */
const applyImport = (queryClient, importId, importData) => {
  if (importData && importData.id === Number(importId)) {
    queryClient.setQueryData(importKeys.detail(importId), importData);
  }

  invalidateImport(queryClient, importId);
};

/**
 * Starts the polling the moment background work is requested. The API writes
 * its busy state before the work begins, so the first poll takes over; locally
 * the request itself blocks until the work finishes, and without this the page
 * would sit still until then.
 */
const markActionPending = (queryClient, importId, action) => {
  queryClient.setQueryData(importKeys.detail(importId), current => (current ? { ...current, pendingAction: action } : current));
};

export const useCreateImport = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ companyId, ...data }) => camelCaseApiResponse(() => portalApi.createImport(companyId, data)),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([...importKeys.all, 'list']);
      },
      ...options
    }
  );
};

/**
 * One hook for every state transition on an import: validate, run, pause,
 * resume, rollback, stageFromDropbox, cancelPull, draftManifest, cancelFill.
 */
export const useImportAction = (importId, options = {}) => {
  const queryClient = useQueryClient();

  const actions = {
    validate: portalApi.validateImport,
    run: portalApi.runImport,
    pause: portalApi.pauseImport,
    resume: portalApi.resumeImport,
    rollback: portalApi.rollbackImport,
    stageFromDropbox: portalApi.stageImportFromDropbox,
    cancelPull: portalApi.cancelImportPull,
    draftManifest: portalApi.draftImportManifest,
    cancelFill: portalApi.cancelClassifyImportFiles
  };

  return useMutation(
    action => camelCaseApiResponse(() => actions[action](importId)),
    {
      onMutate: action => markActionPending(queryClient, importId, action),
      onSuccess: importData => applyImport(queryClient, importId, importData),
      onError: () => invalidateImport(queryClient, importId),
      ...options
    }
  );
};

/**
 * Starts the AI fill-in-the-blanks pass for the chosen tasks; the import polls
 * while the queued pass runs and the rows refresh when it finishes.
 */
export const useClassifyImportFiles = (importId, options = {}) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ all = false, tasks = ['file_type'] } = {}) => camelCaseApiResponse(() => portalApi.classifyImportFiles(importId, { all, tasks })),
    {
      onMutate: () => markActionPending(queryClient, importId, 'classify'),
      onSuccess: importData => applyImport(queryClient, importId, importData),
      onError: () => invalidateImport(queryClient, importId),
      ...options
    }
  );
};

/**
 * Removes staged files and their objects: `{ ids }` for a selection, `{ all: true }`
 * for everything the import has staged.
 */
export const useDeleteImportFiles = (importId, options = {}) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ ids = null, all = false } = {}) => camelCaseApiResponse(() => portalApi.deleteImportFiles(importId, { ids, all })),
    {
      onSuccess: () => invalidateImport(queryClient, importId),
      ...options
    }
  );
};

export const useUpdateImportRow = (importId, options = {}) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ rowId, ...overrides }) => camelCaseApiResponse(() => portalApi.updateImportRow(importId, rowId, overrides)),
    {
      onSuccess: () => invalidateImport(queryClient, importId),
      ...options
    }
  );
};

/* UPLOADS
========================================================= */

/**
 * The manifest goes up through a presigned PUT: register, PUT the bytes, complete.
 */
export const uploadImportManifest = async (importId, file) => {
  const registered = await portalApi.registerImportManifest(importId);
  const uploadUrl = registered.manifestUploadUrl ?? registered.manifest_upload_url;

  const response = await fetch(uploadUrl, { method: 'PUT', body: file });

  if (!response.ok) {
    throw new Error(`Manifest upload failed (${response.status})`);
  }

  return portalApi.completeImportManifest(importId);
};

/**
 * The path the browser reports for a file: react-dropzone keeps a dropped
 * folder's structure on `path`, the directory picker on `webkitRelativePath`.
 */
export const relativePathOf = file => {
  const raw = file.path || file.webkitRelativePath || file.name;

  return raw.replace(/^\.?\//, '');
};

/**
 * Browsers prefix every file in a dropped or picked folder with that folder's
 * own name. The manifest's file_path is relative to the directory being
 * imported (the top folder is the insured), so when every file in the batch
 * shares the same first segment it is dropped, exactly as the CLI sync does.
 *
 * @return array<{file: File, path: string}>
 */
export const batchPaths = files => {
  const paths = files.map(relativePathOf);
  const roots = paths.map(path => (path.includes('/') ? path.split('/')[0] : null));
  const commonRoot = roots.length > 0 && roots.every(root => root !== null && root === roots[0]) ? roots[0] : null;

  return files.map((file, index) => ({
    file,
    path: commonRoot ? paths[index].slice(commonRoot.length + 1) : paths[index]
  }));
};

/**
 * Each file goes up through a presigned POST: register (which returns the form
 * fields S3 expects), POST the fields plus the file, complete.
 */
export const uploadImportFile = async (importId, file, path = relativePathOf(file)) => {
  const registered = await portalApi.registerImportFile(importId, {
    path,
    size: file.size,
    contentType: file.type || null
  });
  const uploadPost = registered.upload_post ?? registered.uploadPost;

  const form = new FormData();

  Object.entries(uploadPost.fields ?? {}).forEach(([key, value]) => form.append(key, value));
  form.append('file', file);

  const response = await fetch(uploadPost.url, { method: 'POST', body: form });

  if (!response.ok) {
    throw new Error(`Upload of ${path} failed (${response.status})`);
  }

  return portalApi.completeImportFile(importId, registered.id);
};

/**
 * Uploads a batch sequentially so a few thousand files never open a few
 * thousand connections; reports progress after each file.
 */
export const useUploadImportFiles = (importId, options = {}) => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ files, onProgress }) => {
      const failures = [];
      const batch = batchPaths(files);

      for (let index = 0; index < batch.length; index += 1) {
        const { file, path } = batch[index];

        try {
          // eslint-disable-next-line no-await-in-loop
          await uploadImportFile(importId, file, path);
        } catch (error) {
          failures.push({ path, message: error.message });
        }

        if (onProgress) {
          onProgress({ done: index + 1, total: files.length, failures });
        }
      }

      return { uploaded: files.length - failures.length, failures };
    },
    {
      onSuccess: () => invalidateImport(queryClient, importId),
      ...options
    }
  );
};

export const useUploadImportManifest = (importId, options = {}) => {
  const queryClient = useQueryClient();

  return useMutation(
    file => uploadImportManifest(importId, file),
    {
      onSuccess: () => invalidateImport(queryClient, importId),
      ...options
    }
  );
};

/* DOWNLOADS
========================================================= */

/**
 * The issues report and the manifest are authenticated CSV streams, so they are
 * fetched with the bearer token and handed to the browser as downloads.
 */
const downloadCsv = async (url, filename, what) => {
  const response = await fetch(url, { headers: getAuthHeaders() });

  if (!response.ok) {
    throw new Error(`${what} download failed (${response.status})`);
  }

  const blob = await response.blob();
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);
};

export const downloadImportReport = importId => downloadCsv(portalApi.importReportUrl(importId), `import-${importId}-issues.csv`, 'Report');

export const downloadImportManifest = importId => downloadCsv(portalApi.importManifestUrl(importId), `import-${importId}-manifest.csv`, 'Manifest');
